/**
 * AgendamentoAuditoresSection - Auditor management section for AgendamentoTab
 */
import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { User } from '../../services/userService';
// Story 2.51: ordenação alfabética PT-BR
const sortAuditores = (list: User[]): User[] => {
  const getDisplayName = (u: User) =>
    u.first_name && u.last_name
      ? `${u.first_name} ${u.last_name}`
      : u.first_name || u.username || '';
  return [...list].sort((a, b) =>
    getDisplayName(a).localeCompare(getDisplayName(b), 'pt-BR', { sensitivity: 'base' })
  );
};

interface AgendamentoAuditoreSectionProps {
  auditoresDisponiveis: User[];
  loadingAuditores: boolean;
  errorAuditores: string | null;
  auditoresParticipantes: User[];
  selectedAuditorId: number;
  setSelectedAuditorId: (id: number) => void;
  handleAddAuditor: () => void;
  handleRemoveAuditor: () => void;
  isReadOnly: boolean;
  modifiedFields: Set<string>;
}

export const AgendamentoAuditoresSection: React.FC<AgendamentoAuditoreSectionProps> = ({
  auditoresDisponiveis,
  loadingAuditores,
  errorAuditores,
  auditoresParticipantes,
  selectedAuditorId,
  setSelectedAuditorId,
  handleAddAuditor,
  handleRemoveAuditor,
  isReadOnly,
  modifiedFields,
}) => {
  const getDisplayName = (auditor: User) =>
    auditor.first_name && auditor.last_name
      ? `${auditor.first_name} ${auditor.last_name}`
      : auditor.first_name || auditor.username;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Gerenciar Auditor
          {loadingAuditores && (
            <span className="ml-2 text-xs text-blue-500 font-normal">Carregando...</span>
          )}
        </label>

        {errorAuditores && !loadingAuditores && (
          <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
            <div className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">⚠️</span>
              <div className="flex-1">
                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">{errorAuditores}</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Verifique se há usuários com perfil "Auditor" ou "Conferente" cadastrados.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <select
            value={selectedAuditorId}
            onChange={(e) => setSelectedAuditorId(Number(e.target.value))}
            disabled={isReadOnly || loadingAuditores}
            className={`flex-1 px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed ${
              errorAuditores && !loadingAuditores ? 'border-amber-300 dark:border-amber-600' : 'border-gray-200 dark:border-gray-600'
            }`}
          >
            {loadingAuditores ? (
              <option value="0">Carregando auditores...</option>
            ) : auditoresDisponiveis.length === 0 ? (
              <option value="0">Nenhum auditor disponível</option>
            ) : (
              <>
                <option value="0">Selecione um auditor...</option>
                {sortAuditores(
                  auditoresDisponiveis.filter(a => !auditoresParticipantes.some(p => p.id === a.id))
                ).map((auditor) => (
                    <option key={auditor.id} value={auditor.id}>
                      {getDisplayName(auditor)}
                      {auditor.hospital_nome ? ` (${auditor.hospital_nome})` : ''}
                    </option>
                  ))}
              </>
            )}
          </select>
          <button
            onClick={handleAddAuditor}
            disabled={isReadOnly}
            className="flex items-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Plus size={16} />
            <span>Adicionar</span>
          </button>
          <button
            onClick={handleRemoveAuditor}
            disabled={isReadOnly || !selectedAuditorId}
            className="flex items-center space-x-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Trash2 size={16} />
            <span>Remover</span>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Auditores Participantes
        </label>
        <div className={`border-2 rounded-lg p-3 bg-gray-50 dark:bg-gray-800 h-[149px] overflow-y-auto ${
          modifiedFields.has('auditoresParticipantes')
            ? 'border-orange-400 dark:border-orange-500 ring-2 ring-orange-200 dark:ring-orange-900/50'
            : 'border-gray-200 dark:border-gray-600'
        }`}>
          {auditoresParticipantes.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-6">
              Nenhum auditor selecionado
            </p>
          ) : (
            auditoresParticipantes.map((auditor) => (
              <div
                key={auditor.id}
                onClick={() => setSelectedAuditorId(auditor.id)}
                className={`px-3 py-2 mb-2 rounded-lg text-sm font-medium shadow-sm cursor-pointer transition-colors ${
                  selectedAuditorId === auditor.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{getDisplayName(auditor)}</span>
                  {auditor.hospital_nome && (
                    <span className="text-xs opacity-75">{auditor.hospital_nome}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
